import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, LogOut, Settings, Sun, Moon, UserIcon, ChevronDown } from 'lucide-react';
import useAuthStore from '../store/authStore';
import useThemeStore from '../store/themeStore';
import Logo from './Logo';

export default function Navbar() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [serviceOpen, setServiceOpen] = useState(false);
  const { user, logout } = useAuthStore();
  const { isDark, toggleTheme } = useThemeStore();
  const [mobileServiceOpen, setMobileServiceOpen] = useState(false);

  const navigate = useNavigate();

  const servicesRef = useRef(null);

  // Close desktop services dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (servicesRef.current && !servicesRef.current.contains(event.target)) {
        setServiceOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
    setSidebarOpen(false);
  };

  const services = [
    { label: "Airplane Tickets", path: "/services/airplane-tickets" },
    { label: "Tour Package", path: "/tours" },
    { label: "Hotel Bookings", path: "/services/hotels" },
    { label: "Visa Assistance Services", path: "/services/visa" },
  ];

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-40 bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-800">
        <div className="container flex items-center justify-between h-16">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 text-2xl font-bold">
            <Logo size={32} />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">

            {/* Pages */}
            {['/', '/about_us', '/tours', '/news', '/contact'].map((path, idx) => {
              const labels = ['Home', 'About Us', 'Tours Packages', 'News & Blogs', 'Contact Us'];
              return (
                <Link
                  key={idx}
                  to={path}
                  className="px-3 py-2 text-gray-700 dark:text-gray-200 hover:text-blue-500 transition-colors"
                >
                  {labels[idx]}
                </Link>
              );
            })}

            {/* DESKTOP SERVICES */}
            <div className="relative" ref={servicesRef}>
              <button
                onClick={() => setServiceOpen(!serviceOpen)}
                className="px-3 py-2 flex items-center gap-1 text-gray-700 dark:text-gray-200 hover:text-blue-500 transition-colors"
              >
                Services
                <ChevronDown
                  className={`w-4 h-4 mt-0.5 transition-transform ${serviceOpen ? 'rotate-180' : ''}`}
                />
              </button>

              <div
                className={`
                  absolute left-0 mt-3 w-64
                  bg-white/90 dark:bg-slate-800/90 backdrop-blur-md
                  rounded-xl shadow-lg py-2
                  transition-all duration-200
                  ${serviceOpen ? 'opacity-100 scale-100 translate-y-0 pointer-events-auto' : 'opacity-0 scale-95 translate-y-2 pointer-events-none'}
                `}
              >
                {services.map((item, i) => (
                  <Link
                    key={i}
                    to={item.path}
                    className="block px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-green-500 dark:hover:bg-slate-700 rounded-lg transition-colors"
                    onClick={() => setServiceOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>

            {user ? (
              <>
                <Link
                  to="/profile"
                  className="flex items-center gap-2 px-3 py-2 hover:text-blue-500 transition-colors"
                >
                  <UserIcon className="w-4 h-4" />
                  {user.name}
                </Link>

                {user.role === 'admin' && (
                  <Link
                    to="/admin"
                    className="flex items-center gap-2 px-3 py-2 text-green-600 dark:text-green-400 hover:text-green-700 transition-colors"
                  >
                    <Settings className="w-4 h-4" />
                    Admin
                  </Link>
                )}

                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-3 py-2 text-red-600 dark:text-red-400 hover:text-red-700 transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="px-3 py-2 hover:text-blue-500 transition-colors">
                  Login
                </Link>
                <Link to="/register" className="btn-primary">
                  Register
                </Link>
              </>
            )}
          </div>

          {/* Mobile Buttons */}
          <div className="md:hidden flex items-center gap-2">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>

            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
            >
              {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* MOBILE SIDEBAR */}
        <div
          className={`fixed top-0 left-0 h-full w-72 bg-[#052A36] dark:bg-slate-900 shadow-lg z-50 transform transition-transform duration-300 
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:hidden`}
        >
          <div className="flex justify-between items-center p-4 border-b border-gray-200/20">
            <Logo size={32} />
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 hover:bg-[#063442] rounded-lg transition-colors"
            >
              <X className="w-6 h-6 text-white" />
            </button>
          </div>

          <div className="flex flex-col p-4 gap-3 text-white">

            {/* Basic links */}
            {['/', '/about_us', '/tours', '/news', '/contact'].map((path, idx) => {
              const labels = ['Home', 'About Us', 'Tours Packages', 'News & Blogs', 'Contact Us'];
              return (
                <Link
                  key={idx}
                  to={path}
                  className="px-4 py-2 rounded-lg hover:bg-[#063442] transition-colors"
                  onClick={() => setSidebarOpen(false)}
                >
                  {labels[idx]}
                </Link>
              );
            })}

            {/* MOBILE SERVICES */}
           {/* MOBILE SERVICES */}
<div>
  <button
    onClick={() => setMobileServiceOpen(!mobileServiceOpen)}
    className="flex justify-between items-center w-full px-4 py-2 rounded-lg hover:bg-[#063442] transition-colors"
  >
    <span>Services</span>
    <ChevronDown
      className={`w-4 h-4 transition-transform ${mobileServiceOpen ? 'rotate-180' : ''}`}
    />
  </button>

  <div
    className={`
      overflow-hidden transition-all duration-300 ml-3 
      ${mobileServiceOpen ? 'max-h-96 mt-2' : 'max-h-0'}
    `}
  >
    {services.map((item, i) => (
      <Link
        key={i}
        to={item.path}
        className="block px-4 py-2 rounded-lg hover:bg-[#063442] text-sm transition-colors"
        onClick={() => {
          setSidebarOpen(false);
          setMobileServiceOpen(false); // close mobile dropdown on link click
        }}
      >
        {item.label}
      </Link>
    ))}
  </div>
</div>


            <hr className="border-gray-200/20 my-2" />

            {/* MOBILE USER */}
            {user ? (
              <>
                <div className="px-4 py-2 bg-[#063442] rounded-lg text-sm">{user.name}</div>

                <Link
                  to="/profile"
                  className="flex items-center gap-3 px-4 py-2 hover:bg-[#063442] rounded-lg"
                  onClick={() => setSidebarOpen(false)}
                >
                  <UserIcon className="w-4 h-4" />
                  Profile
                </Link>

                {user.role === 'admin' && (
                  <Link
                    to="/admin"
                    className="flex items-center gap-3 px-4 py-2 text-green-300 hover:bg-green-900/20 rounded-lg"
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Settings className="w-4 h-4" />
                    Admin
                  </Link>
                )}

                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 px-4 py-2 text-red-300 hover:bg-red-900/20 rounded-lg"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="px-4 py-2 hover:bg-[#063442] rounded-lg"
                  onClick={() => setSidebarOpen(false)}
                >
                  Login
                </Link>

                <Link
                  to="/register"
                  className="btn-primary text-center"
                  onClick={() => setSidebarOpen(false)}
                >
                  Register
                </Link>
              </>
            )}
          </div>
        </div>

        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </nav>
    </>
  );
}
